import type { PresetActionId, PresetOption } from "@/types/createAgent";
import {
  GENERATE_BACK_LABEL,
  GENERATE_FRONT_LABEL,
  MODIFY_BACK_LABEL,
  MODIFY_FRONT_LABEL,
} from "@/lib/agent/labels";

/**
 * The render actions presented by the desktop designer's stable footer.
 *
 * Agent-authored preset labels are compatibility data and may vary across
 * restored conversations. The designer UI always resolves these action IDs
 * through this registry so desktop and mobile name the same action the same
 * way.
 */
export const DESIGNER_RENDER_ACTION_PRESENTATION = {
  generate_dress_front: {
    label: GENERATE_FRONT_LABEL,
    side: "front",
    mode: "generate",
    desktopOwner: "footer",
  },
  generate_dress_back: {
    label: GENERATE_BACK_LABEL,
    side: "back",
    mode: "generate",
    desktopOwner: "footer",
  },
  modify_dress_front: {
    label: MODIFY_FRONT_LABEL,
    side: "front",
    mode: "regenerate",
    desktopOwner: "footer",
  },
  modify_dress_back: {
    label: MODIFY_BACK_LABEL,
    side: "back",
    mode: "regenerate",
    desktopOwner: "footer",
  },
} as const satisfies Partial<
  Record<
    PresetActionId,
    {
      label: string;
      side: "front" | "back";
      mode: "generate" | "regenerate";
      desktopOwner: "footer";
    }
  >
>;

export type DesignerRenderActionId =
  keyof typeof DESIGNER_RENDER_ACTION_PRESENTATION;

export function isDesignerRenderActionId(
  actionId: PresetActionId | string
): actionId is DesignerRenderActionId {
  return actionId in DESIGNER_RENDER_ACTION_PRESENTATION;
}

export function getDesignerRenderActionLabel(
  actionId: PresetActionId | string
): string | null {
  return isDesignerRenderActionId(actionId)
    ? DESIGNER_RENDER_ACTION_PRESENTATION[actionId].label
    : null;
}

export function isDesktopFooterOwnedAction(
  actionId: PresetActionId | string
): boolean {
  return (
    isDesignerRenderActionId(actionId) &&
    DESIGNER_RENDER_ACTION_PRESENTATION[actionId].desktopOwner === "footer"
  );
}

const LEGACY_MODIFY_COMMIT_LABELS = new Set([
  "apply changes",
  "apply changes to back image",
  MODIFY_FRONT_LABEL.toLowerCase(),
  MODIFY_BACK_LABEL.toLowerCase(),
]);

/** Distinguishes a paid regeneration commit from legacy modify-choice rows. */
export function isDesignerRenderCommitPreset(
  preset: Pick<PresetOption, "actionId" | "actionData" | "label" | "presetType">
): boolean {
  if (!isDesignerRenderActionId(preset.actionId)) return false;
  if (
    preset.actionId !== "modify_dress_front" &&
    preset.actionId !== "modify_dress_back"
  ) {
    return true;
  }
  return (
    preset.presetType === "commit_action" ||
    Boolean(preset.actionData?.designDelta) ||
    LEGACY_MODIFY_COMMIT_LABELS.has(preset.label.trim().toLowerCase())
  );
}

/**
 * Legacy choice rows sometimes reused a modify action ID before staging the
 * actual paid edit. Only an explicit commit preset belongs to the desktop
 * footer; those choice rows must remain available in chat.
 */
export function isDesktopFooterOwnedPreset(
  preset: Pick<PresetOption, "actionId" | "actionData" | "label" | "presetType">
): boolean {
  return (
    isDesktopFooterOwnedAction(preset.actionId) &&
    isDesignerRenderCommitPreset(preset)
  );
}
